import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';

interface ListingData {
  id: number;
  title: string;
  slug?: string;
  category_id: string;
  region_id: number | null;
  phone: string;
  email: string;
  website: string;
  description: string;
  image_url: string;
  services: string[];
  badge: string;
  category_name?: string;
  region_name?: string;
}

interface FormState {
  title: string;
  category_id: string;
  region_id: string;
  phone: string;
  email: string;
  website: string;
  description: string;
  image_url: string;
  services: string[];
}

export default function EditListing() {
  const [token, setToken] = useState<string | null>(null);
  const [listing, setListing] = useState<ListingData | null>(null);
  const [categories, setCategories] = useState<{ id: string; name: string; slug: string }[]>([]);
  const [regions, setRegions] = useState<{ id: number; name: string }[]>([]);
  const [form, setForm] = useState<FormState | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [serviceInput, setServiceInput] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const t = localStorage.getItem('marinelink_owner_token');
    if (!t) { navigate('/signin'); return; }
    setToken(t);
  }, [navigate]);

  useEffect(() => {
    if (!token) return;
    fetch('/.netlify/functions/auth-listing', { headers: { Authorization: `Bearer ${token}` } })
      .then(res => res.json())
      .then(d => {
        if (d.error) { setError(d.error); return; }
        setListing(d.listing);
        setCategories(d.categories || []);
        setRegions(d.regions || []);
        setForm({
          title: d.listing.title || '',
          category_id: d.listing.category_id || '',
          region_id: d.listing.region_id ? String(d.listing.region_id) : '',
          phone: d.listing.phone || '',
          email: d.listing.email || '',
          website: d.listing.website || '',
          description: d.listing.description || '',
          image_url: d.listing.image_url || '',
          services: d.listing.services || [],
        });
      })
      .catch(() => setError('Failed to load listing data'))
      .finally(() => setLoading(false));
  }, [token]);

  const update = (field: keyof FormState, value: string | string[]) => {
    if (!form) return;
    setForm({ ...form, [field]: value });
    setSuccess(false);
  };

  const addService = () => {
    const s = serviceInput.trim();
    if (!s || !form) return;
    if (form.services.includes(s)) { setServiceInput(''); return; }
    update('services', [...form.services, s]);
    setServiceInput('');
  };

  const removeService = (s: string) => {
    if (!form) return;
    update('services', form.services.filter(x => x !== s));
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form || !token) return;
    setSaving(true);
    setError('');
    setSuccess(false);
    try {
      const res = await fetch('/.netlify/functions/auth-listing', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Save failed'); return; }
      setSuccess(true);
      window.scrollTo(0, 0);
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="max-w-[800px] mx-auto px-4 py-12 text-center text-text-muted">Loading editor…</div>;
  if (error && !listing) return <div className="max-w-[800px] mx-auto px-4 py-12"><div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 text-red-700 dark:text-red-300">{error}</div></div>;
  if (!form) return null;

  return (
    <div className="max-w-[800px] mx-auto px-4 md:px-8 py-6 md:py-12 pb-24">
      <div className="flex items-center gap-2 text-sm text-text-muted mb-4">
        <Link to="/dashboard" className="text-text-secondary hover:text-accent transition-colors">Dashboard</Link>
        <span>/</span>
        <span>Edit Listing</span>
      </div>
      <h1 className="font-display text-2xl md:text-[1.8rem] font-bold mb-6">Edit Your Listing</h1>

      {success && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3 mb-4 text-sm text-green-700 dark:text-green-300">
          ✓ Listing updated successfully.
        </div>
      )}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 mb-4 text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      <form onSubmit={handleSave} className="space-y-6">
        {/* Business name */}
        <div>
          <label className="block text-xs font-semibold text-text-secondary mb-2">Business Name *</label>
          <input className="form-input" required value={form.title} onChange={e => update('title', e.target.value)} maxLength={200} />
        </div>

        {/* Category + Region */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <label className="block text-xs font-semibold text-text-secondary mb-2">Category</label>
            <select className="form-input" value={form.category_id} onChange={e => update('category_id', e.target.value)}>
              <option value="">Select category…</option>
              {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-text-secondary mb-2">Region</label>
            <select className="form-input" value={form.region_id} onChange={e => update('region_id', e.target.value)}>
              <option value="">Select region…</option>
              {regions.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
          </div>
        </div>

        {/* Contact details */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <label className="block text-xs font-semibold text-text-secondary mb-2">Phone</label>
            <input className="form-input" type="tel" value={form.phone} onChange={e => update('phone', e.target.value)} placeholder="09 123 4567" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-text-secondary mb-2">Public Email</label>
            <input className="form-input" type="email" value={form.email} onChange={e => update('email', e.target.value)} placeholder="info@business.co.nz" />
          </div>
        </div>

        {/* Website */}
        <div>
          <label className="block text-xs font-semibold text-text-secondary mb-2">Website</label>
          <input className="form-input" type="url" value={form.website} onChange={e => update('website', e.target.value)} placeholder="https://www.business.co.nz" />
        </div>

        {/* Primary image URL */}
        <div>
          <label className="block text-xs font-semibold text-text-secondary mb-2">Primary Image URL</label>
          <input className="form-input" type="url" value={form.image_url} onChange={e => update('image_url', e.target.value)} placeholder="https://…" />
          {form.image_url && (
            <img src={form.image_url} alt="Preview" className="mt-2 w-32 h-32 rounded-lg object-cover border border-[rgba(79,179,217,0.14)]" />
          )}
          <p className="text-xs text-text-muted mt-1">This is the main image shown on your listing card. Use the Gallery page to manage multiple photos.</p>
        </div>

        {/* Description */}
        <div>
          <label className="block text-xs font-semibold text-text-secondary mb-2">Description</label>
          <textarea
            className="form-input min-h-[140px] resize-y"
            value={form.description}
            onChange={e => update('description', e.target.value)}
            maxLength={3000}
            placeholder="Describe your business, services, and what makes you stand out…"
          />
          <p className="text-xs text-text-muted mt-1">{form.description.length} / 3000 characters</p>
        </div>

        {/* Services */}
        <div>
          <label className="block text-xs font-semibold text-text-secondary mb-2">Services / Tags</label>
          <div className="flex gap-2 mb-2">
            <input
              className="form-input flex-1"
              value={serviceInput}
              onChange={e => setServiceInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addService(); } }}
              placeholder="e.g. Boat repairs, Marina berths, Chandlery"
              maxLength={100}
            />
            <button type="button" onClick={addService} className="btn btn-ghost btn-sm">Add</button>
          </div>
          {form.services.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {form.services.map(s => (
                <span key={s} className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-accent-glow text-sm">
                  {s}
                  <button type="button" onClick={() => removeService(s)} className="text-text-muted hover:text-red-500" aria-label={`Remove ${s}`}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-3 pt-2">
          <button type="submit" disabled={saving} className="btn btn-primary btn-lg flex-1">
            {saving ? 'Saving…' : 'Save changes'}
          </button>
          <Link to="/dashboard" className="btn btn-ghost btn-lg">Cancel</Link>
        </div>
      </form>
    </div>
  );
}
